import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, BadgeCheck, Sparkles, Heart, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

// Curated category → photo map. When a verified Hooman lacks portfolio photos,
// we pick a tasteful stock shot that matches their craft so the hero stays beautiful.
const CATEGORY_PHOTO = {
  "Photographer":     "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=900&q=80",
  "Surf Instructor":  "https://images.unsplash.com/photo-1528892952291-009c663ce843?w=900&q=80",
  "Ceramicist":       "https://images.unsplash.com/photo-1590605095243-072811dbe64c?w=900&q=80",
  "Chef":             "https://images.unsplash.com/photo-1581349485608-9469926a8e5e?w=900&q=80",
  "Tattoo Artist":    "https://images.unsplash.com/photo-1568515045052-f9a854d70bfd?w=900&q=80",
  "Yoga Teacher":     "https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=900&q=80",
  "Musician":         "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=900&q=80",
  "Carpenter":        "https://images.unsplash.com/photo-1503602642458-232111445657?w=900&q=80",
  "Barber":           "https://images.unsplash.com/photo-1454425064867-99b34cf1cbed?w=900&q=80",
  "Designer":         "https://images.unsplash.com/photo-1556157382-97eda2d62296?w=900&q=80",
  "Florist":          "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=900&q=80",
  "Dancer":           "https://images.unsplash.com/photo-1488751045188-3c55bbf9a3fa?w=900&q=80",
  "Baker":            "https://images.unsplash.com/photo-1518609878373-06d740f60d8b?w=900&q=80",
};
const FALLBACK_PHOTO = "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=900&q=80";

function pickHeroPhoto(user) {
  // 1. Prefer the user's hero/cover image if it's a real URL — this is their curated banner.
  const hero = user.hero_image;
  if (typeof hero === "string" && hero.startsWith("http")) return hero;
  // 2. Then the first real (non-data:) portfolio photo.
  const port = (user.portfolio || []).find((p) => typeof p === "string" && p.startsWith("http"));
  if (port) return port;
  // 3. Then the user's avatar if it's a real URL (Unsplash portrait, uploaded photo, etc.).
  //    Skip tiny base64 data: stubs from seeds — they look terrible when blown up.
  if (typeof user.avatar === "string" && user.avatar.startsWith("http")) {
    // Upgrade Unsplash thumbnails to a larger crop for the hero tile
    if (user.avatar.includes("images.unsplash.com")) {
      return user.avatar.replace(/([?&])w=\d+/, "$1w=900").replace(/([?&])q=\d+/, "$1q=85");
    }
    return user.avatar;
  }
  // 4. Category-matched stock as a last resort.
  const cat = user.category || (user.categories || [])[0] || "";
  return CATEGORY_PHOTO[cat] || FALLBACK_PHOTO;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const SKILLS = [
  "Surf instructors", "Photographers", "Tattoo artists", "Chefs", "Ceramicists",
  "Yoga teachers", "Carpenters", "Designers", "Tutors", "Dog trainers",
  "Florists", "Mechanics", "Musicians", "Coaches", "Gardeners", "Developers",
];

export default function Landing() {
  const [reviews, setReviews] = useState([]);
  const [hoomans, setHoomans] = useState([]);
  const { t } = useI18n();

  useEffect(() => { api.get("/reviews/recent?limit=6").then(r=>setReviews(r.data)).catch(()=>{}); }, []);
  useEffect(() => {
    // Load verified humans for the hero. Prefer verified, fall back to is_hooman=true.
    (async () => {
      try {
        const { data } = await api.get("/users?is_hooman=true&limit=16");
        const verified = (data || []).filter((u) => u.verified);
        const pool = verified.length >= 4 ? verified : (data || []);
        setHoomans(shuffle(pool).slice(0, 4));
      } catch { setHoomans([]); }
    })();
  }, []);

  // Rotate one tile every 6s so the hero feels alive
  const [tick, setTick] = useState(0);
  useEffect(() => { const iv = setInterval(()=>setTick(t=>t+1), 6000); return ()=>clearInterval(iv); }, []);
  const rotated = useMemo(() => {
    if (hoomans.length < 4) return hoomans;
    return hoomans;
  }, [hoomans, tick]);
  return (
    <div className="bg-[#FAFAFA]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-20 sm:pt-28 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 rise">
            <p className="overline text-[#0057FF] mb-6" data-testid="hero-tagline">Real Skills. Real People.</p>
            <h1 className="h-display text-5xl sm:text-6xl md:text-7xl">
              {t("hero.title.1")} <span className="italic font-normal text-[#0057FF]">{t("hero.title.2")}</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-[#4A4A4A] leading-relaxed">
              {t("hero.sub")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/discover" className="btn-primary" data-testid="hero-browse-cta">
                {t("hero.browse")} <ArrowRight size={18}/>
              </Link>
              <Link to="/register" className="btn-secondary" data-testid="hero-become-cta">
                {t("hero.become")}
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-[#4A4A4A]">
              <span className="inline-flex items-center gap-2"><BadgeCheck size={16} className="text-[#050505]"/> {t("hero.verified")}</span>
              <span className="inline-flex items-center gap-2"><Heart size={16} className="text-[#0057FF]"/> {t("hero.novanity")}</span>
            </div>
          </div>

          <div className="lg:col-span-5 rise delay-2">
            <div className="relative">
              <div className="absolute -top-6 -left-6 h-40 w-40 rounded-full bg-[#0057FF]/15 blur-2xl" />
              <div className="absolute -bottom-10 -right-6 h-52 w-52 rounded-full bg-[#0A0A0A]/15 blur-3xl" />
              <div className="grid grid-cols-2 gap-4" data-testid="hero-hoomans-grid">
                {rotated.length >= 4 ? (
                  <>
                    <HoomanTile user={rotated[0]} className="h-64 mt-0" />
                    <HoomanTile user={rotated[1]} className="h-64 mt-10" />
                    <HoomanTile user={rotated[2]} className="h-56 -mt-6" />
                    <HoomanTile user={rotated[3]} className="h-56 mt-0" />
                  </>
                ) : (
                  // Skeleton while hoomans are loading
                  <>
                    <div className="h-64 mt-0  rounded-3xl bg-[#F2F2F0] animate-pulse" />
                    <div className="h-64 mt-10 rounded-3xl bg-[#F2F2F0] animate-pulse" />
                    <div className="h-56 -mt-6 rounded-3xl bg-[#F2F2F0] animate-pulse" />
                    <div className="h-56 mt-0  rounded-3xl bg-[#F2F2F0] animate-pulse" />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#F2F2F0] py-10 overflow-hidden border-y border-[#E5E5E3]">
        <div className="marquee whitespace-nowrap font-heading text-3xl sm:text-4xl text-[#0A0A0A]/80">
          {[...SKILLS, ...SKILLS].map((s, i) => (
            <span key={i} className="inline-flex items-center gap-24">
              {s}
              <span className="text-[#0057FF]">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
        <div className="max-w-3xl">
          <p className="overline mb-5">Why Hoomanz</p>
          <h2 className="h-display text-4xl sm:text-5xl">A profile, not an algorithm.</h2>
          <p className="mt-6 text-lg text-[#4A4A4A]">
            Replace your Instagram, Calendly, Linktree, and personal site with one human-first home.
            Get discovered, get booked, get paid, build a reputation that's actually yours.
          </p>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {[
            { t:"Be discovered", d:"Get found by people nearby who need what only you do.", c:"#0A0A0A" },
            { t:"Book without friction", d:"Calendar, payments, reminders — all in one warm flow.", c:"#0057FF" },
            { t:"Earn real trust", d:"Verified reviews and a trust score that means something.", c:"#0A0A0A" },
          ].map((v,i)=>(
            <div key={i} className="card p-8 rise" style={{animationDelay:`${i*0.08}s`}}>
              <div className="h-10 w-10 rounded-full mb-6" style={{background:`${v.c}1A`, color:v.c}}>
                <div className="h-full w-full rounded-full flex items-center justify-center font-heading">0{i+1}</div>
              </div>
              <h3 className="font-heading text-2xl">{v.t}</h3>
              <p className="text-[#4A4A4A] mt-3">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS / REAL STORIES */}
      <section className="bg-[#F2F2F0] border-y border-[#E5E5E3]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <p className="overline mb-4">Real stories</p>
              <h2 className="h-display text-4xl sm:text-5xl">What Humans say about Hoomanz.</h2>
            </div>
            <Link to="/discover" className="btn-secondary" data-testid="reviews-browse-cta">Find someone you'll love →</Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="landing-reviews">
            {reviews.map((r) => (
              <Link to={`/u/${r.hooman?.id}`} key={r.id} className="card p-7 block" data-testid={`landing-review-${r.id}`}>
                <div className="flex items-center gap-1 text-[#0057FF] mb-4">
                  {Array.from({length:r.rating}).map((_,i)=><Star key={i} size={14} className="fill-[#0057FF] stroke-[#0057FF]"/>)}
                </div>
                <p className="text-lg font-heading leading-snug text-[#0A0A0A]">"{r.text}"</p>
                <div className="mt-6 pt-5 border-t border-[#E5E5E3] flex items-center gap-3">
                  {r.reviewer?.avatar
                    ? <img src={r.reviewer.avatar} alt="" className="h-9 w-9 rounded-full object-cover"/>
                    : <div className="h-9 w-9 rounded-full bg-[#E5E5E3]"/>}
                  <div className="text-sm flex-1 min-w-0">
                    <p className="font-medium truncate">{r.reviewer?.name}</p>
                    <p className="text-[#8A8A8A] truncate">for {r.hooman?.name} · {r.hooman?.category}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="overline text-[#0057FF] mb-5">Make a living doing what you love</p>
            <h2 className="h-display text-4xl sm:text-5xl">Your profile, your business, your terms.</h2>
            <p className="mt-6 text-white/70 text-lg max-w-md">
              Build the operating system for your independent work in fifteen minutes.
            </p>
            <div className="mt-10">
              <Link to="/register" className="btn-accent" data-testid="cta-bottom-join">Become a Hooman</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img className="rounded-3xl object-cover h-72 w-full" src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&q=80" alt=""/>
            <img className="rounded-3xl object-cover h-72 w-full mt-12" src="https://images.unsplash.com/photo-1528892952291-009c663ce843?w=600&q=80" alt=""/>
          </div>
        </div>
      </section>
    </div>
  );
}

// HoomanTile — a verified human card linking to their profile, with name + profession overlay.
function HoomanTile({ user, className = "" }) {
  const [imgSrc, setImgSrc] = useState(() => pickHeroPhoto(user));
  const FALLBACKS = [
    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=900&q=80",
    "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=900&q=80",
    "https://images.unsplash.com/photo-1590605095243-072811dbe64c?w=900&q=80",
    "https://images.unsplash.com/photo-1581349485608-9469926a8e5e?w=900&q=80",
  ];
  const onErr = (e) => {
    e.target.onerror = null;
    const f = FALLBACKS[Math.floor(Math.random() * FALLBACKS.length)];
    setImgSrc(f);
    e.target.src = f;
  };
  const profession = user.category || (user.categories || [])[0] || "Hooman";
  return (
    <Link
      to={`/u/${user.id}`}
      data-testid={`hero-hooman-${user.id}`}
      aria-label={`${user.name} — ${profession}. View profile.`}
      className={`group relative rounded-3xl overflow-hidden bg-[#F2F2F0] w-full block transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl ${className}`}
    >
      <img
        src={imgSrc}
        alt={`${user.name} — ${profession}`}
        loading="lazy"
        onError={onErr}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      {/* Gradient overlay for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />
      {/* Verified badge top-right */}
      {user.verified && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-white/95 backdrop-blur text-[10px] uppercase tracking-wider font-medium px-2 py-1 rounded-full text-[#0A0A0A]">
          <BadgeCheck size={11} className="text-[#0A0A0A]"/> Verified
        </span>
      )}
      {/* Caption */}
      <div className="absolute inset-x-0 bottom-0 p-4">
        <p className="text-white font-medium text-base leading-tight truncate" data-testid={`hero-hooman-name-${user.id}`}>
          {user.name}
        </p>
        <p className="text-white/85 text-xs mt-0.5 truncate">{profession}{user.location ? ` · ${user.location.split(",")[0]}` : ""}</p>
      </div>
    </Link>
  );
}
